<template>
  <v-dialog v-model="dialog" max-width="1100px" persistent scrollable>
    <v-card class="d-flex flex-column" :style="{ maxHeight: '88vh', height: step === 2 ? '88vh' : 'auto' }">
      <!-- Header -->
      <v-card-title class="d-flex align-center justify-space-between pa-4">
        <div class="d-flex align-center">
          <v-icon class="mr-3" color="primary">mdi-table-arrow-right</v-icon>
          <div>
            <h3 class="mb-0">{{ stepTitle }}</h3>
            <p class="text-caption mb-0 text--secondary">{{ stepDescription }}</p>
          </div>
        </div>
        <v-btn icon @click="close" :disabled="importing"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <!-- Progress -->
      <div class="px-6 pt-2 pb-3">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center" style="gap: 28px">
            <div
              v-for="(s, i) in steps"
              :key="i"
              class="d-flex align-center"
              style="gap: 8px">
              <div class="step-circle" :class="stepClass(i + 1)">
                <v-icon v-if="step > i + 1" size="15" color="white">mdi-check</v-icon>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="text-body-2" :class="{ 'font-weight-bold': step === i + 1 }">{{ s }}</span>
            </div>
          </div>
          <span class="text-caption text--secondary">Step {{ step }} of {{ steps.length }}</span>
        </div>
        <v-progress-linear
          :value="((step - 1) / (steps.length - 1)) * 100"
          height="4"
          rounded
          color="primary"
          background-color="grey lighten-3" />
      </div>
      <v-divider />

      <v-card-text class="flex-grow-1 pa-0" style="overflow-y: auto">
        <!-- STEP 1 — Upload -->
        <div v-if="step === 1" class="pa-6">
          <v-alert type="info" text dense class="mb-4" icon="mdi-information-outline">
            Imports the full hierarchy — <strong>CMLs</strong>, their <strong>measurement points (TML)</strong>, and
            <strong>thickness readings</strong> — upserting under each Master Tag. Supported: <strong>.xlsx, .xls, .csv</strong>.
          </v-alert>

          <div
            class="drop-area"
            :class="{ 'drag-active': dragOver, 'has-file': selectedFile }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
            @click="triggerFileInput">
            <template v-if="!selectedFile && !fileProcessing">
              <v-icon size="56" color="primary lighten-1">mdi-cloud-upload-outline</v-icon>
              <h4 class="mt-2">Choose or drag your spreadsheet</h4>
              <p class="text--secondary mb-0">Click here or drop a .xlsx / .xls / .csv file</p>
            </template>
            <template v-else-if="fileProcessing">
              <v-progress-circular indeterminate color="primary" size="48" />
              <h4 class="mt-3">Reading your file…</h4>
            </template>
            <template v-else>
              <v-icon size="44" color="success">mdi-file-check-outline</v-icon>
              <h4 class="mt-2">{{ selectedFile.name }}</h4>
              <p class="text--secondary mb-1">{{ formatSize(selectedFile.size) }}</p>
              <v-btn small outlined color="primary" @click.stop="clearFile">Choose a different file</v-btn>
            </template>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            style="display: none"
            @change="onFileInputChange" />

          <div class="d-flex align-center justify-center mt-4">
            <span class="text-caption text--secondary mr-1">Don't have a file to start from?</span>
            <v-btn text small color="primary" class="text-none px-2" @click="downloadTemplate">
              <v-icon left small>mdi-download-outline</v-icon> Download a template
            </v-btn>
          </div>

          <div class="d-flex justify-end mt-6">
            <v-btn
              color="primary"
              large
              :disabled="!selectedFile || fileProcessing"
              :loading="fileProcessing"
              @click="processFile">
              <v-icon left>mdi-cog-outline</v-icon> Process File
            </v-btn>
          </div>
        </div>

        <!-- STEP 2 — Map -->
        <div v-else-if="step === 2" class="pa-6">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="section-label">Source system</div>
              <v-select
                v-model="externalRefSource"
                :items="sourceSystems"
                dense outlined hide-details
                label="Where is this data from?" />
              <p class="text-caption text--secondary mt-1 mb-0">
                Stamped on each CML so re-imports update (not duplicate) existing rows.
              </p>
            </v-col>
          </v-row>

          <div class="section-label mt-6">Identity columns <span class="red--text">*</span></div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="masterTagColumn"
                :items="columnItems"
                dense outlined
                :error="!masterTagColumn"
                label="Master Tag column (required)"
                hint="Resolved to equipment in this facility; rows whose tag isn't found are rejected."
                persistent-hint />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="externalRefColumn"
                :items="columnItems"
                dense outlined clearable
                label="External Ref column (CML id at source)"
                hint="Used to match existing CMLs for upsert. Blank rows are inserted as new."
                persistent-hint />
            </v-col>
          </v-row>

          <div class="section-label mt-6">CML fields</div>
          <v-row dense>
            <v-col v-for="f in cmlFields" :key="f.field" cols="12" md="6">
              <v-select
                v-model="fieldColumns[f.field]"
                :items="columnItems"
                dense outlined clearable hide-details
                :label="f.label" />
            </v-col>
          </v-row>

          <div class="section-label mt-6">Measurement points (TML)</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="tmlPointNoColumn"
                :items="columnItems"
                dense outlined clearable
                label="TML Point No. column"
                hint="Optional. Creates a measurement point per row under its CML; re-import matches existing points instead of duplicating."
                persistent-hint />
            </v-col>
          </v-row>

          <div class="section-label mt-6">Thickness readings</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="readingValueColumn"
                :items="columnItems"
                dense outlined clearable hide-details
                label="Reading value column (mm)" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="readingDateColumn"
                :items="columnItems"
                dense outlined clearable
                label="Reading date column"
                hint="Needs both value and date, plus a mapped TML Point No. One reading per date is kept — re-import adds only new dates."
                persistent-hint />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="inspectionNoColumn"
                :items="columnItems"
                dense outlined clearable
                label="Inspection No. column (optional)"
                :hint="inspectionNoHint"
                persistent-hint />
            </v-col>
            <!-- Auto-create writes new inspections, so the switch is hidden without
                 the create permission. The server re-checks it (403 otherwise) —
                 hiding here is convenience, not the authorization boundary. -->
            <v-col v-if="canAutoCreateInspections" cols="12" md="6">
              <v-switch
                v-model="autoCreateInspections"
                :disabled="!inspectionNoColumn"
                label="Auto-create missing inspections"
                color="primary"
                dense hide-details inset
                class="mt-1 cml-import-switch" />
              <p class="cml-import-hint mt-2 mb-0">{{ autoCreateInspectionsHint }}</p>
            </v-col>
          </v-row>

          <div v-if="excelData" class="text-caption text--secondary mt-4">
            {{ excelData.data.length }} data row(s) · {{ columns.length }} column(s) detected.
          </div>
          <div class="text-caption text--secondary mt-1">
            <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
            Date columns in <strong>CSV</strong> files must be <strong>dd/MM/yyyy</strong>; Excel date cells are read automatically.
          </div>

          <div class="d-flex justify-space-between mt-6">
            <v-btn text @click="step = 1" :disabled="importing"><v-icon left>mdi-arrow-left</v-icon> Back</v-btn>
            <v-btn color="primary" large :disabled="!canImport" :loading="importing" @click="performImport">
              <v-icon left>mdi-database-import-outline</v-icon> Run Import
            </v-btn>
          </div>
          <p v-if="!masterTagColumn" class="text-caption red--text text-right mt-2 mb-0">
            Map the Master Tag column to continue.
          </p>
        </div>

        <!-- STEP 3 — Results -->
        <div v-else-if="step === 3" class="cml-results-container pa-5">
          <section class="cml-results-hero" :class="`cml-results-hero--${resultState.tone || 'info'}`">
            <div class="cml-results-hero__summary">
              <v-icon size="28" :color="resultState.color">{{ resultState.icon }}</v-icon>
              <div>
                <div class="text-subtitle-1 font-weight-bold">{{ resultState.title }}</div>
                <div class="text-caption text--secondary">{{ resultMessage }}</div>
              </div>
            </div>
            <div class="cml-results-hero__badges">
              <v-chip v-if="appliedCmlRowCount" small label color="primary" text-color="white">
                {{ appliedCmlRowCount }} CML Rows Applied
              </v-chip>
              <v-chip
                v-if="itemizedIssues.length"
                small
                label
                :color="errors.length ? 'error' : 'warning'"
                text-color="white">
                {{ itemizedIssues.length }} {{ errors.length ? 'Issues' : 'Warnings' }}
              </v-chip>
            </div>
          </section>

          <v-alert v-if="resultState.alert" :type="resultState.alert" text dense class="mb-0">
            {{ resultMessage }}
          </v-alert>

          <section v-if="hierarchyRows.length && resultState.showStats" class="cml-results-section">
            <header class="cml-results-section__header">
              <span><v-icon x-small class="mr-1">mdi-layers-outline</v-icon>Ingestion hierarchy summary</span>
              <span v-if="excelData && excelData.data">{{ excelData.data.length }} Spreadsheet Rows</span>
            </header>
            <div class="cml-results-table-wrap">
              <table class="cml-results-table">
                <thead>
                  <tr>
                    <th>Target</th>
                    <th>Operation</th>
                    <th class="text-right">Count</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in hierarchyRows" :key="`${row.layer}-${row.action}`">
                    <td><v-icon small :color="row.iconColor" class="mr-2">{{ row.icon }}</v-icon><strong>{{ row.layer }}</strong></td>
                    <td>{{ row.action }}</td>
                    <td class="text-right font-weight-bold cml-results-mono" :class="row.countClass">{{ row.count }}</td>
                    <td class="text-center">
                      <span class="cml-results-pill" :class="`cml-results-pill--${row.statusType}`">{{ row.statusText }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="itemizedIssues.length" class="cml-results-section">
            <header class="cml-results-section__header">
              <span><v-icon x-small :color="errors.length ? 'error' : 'warning'" class="mr-1">mdi-alert-outline</v-icon>Issues and warnings</span>
              <span>Individual import details</span>
            </header>
            <div class="cml-results-table-wrap">
              <table class="cml-results-table cml-results-table--issues">
                <thead>
                  <tr>
                    <th>Scope</th>
                    <th>Type</th>
                    <th>Diagnostic message</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="issue in itemizedIssues">
                    <tr :key="issue.id">
                      <td><span class="cml-results-code">{{ issue.rowLabel }}</span></td>
                      <td><span class="cml-results-code" :class="`cml-results-code--${issue.statusType}`">{{ issue.category }}</span></td>
                      <td>
                        <div>{{ issue.message }}</div>
                        <v-btn
                          v-if="issue.rowData"
                          x-small
                          text
                          color="primary"
                          class="px-1 text-none"
                          @click="toggleRowData(issue.id)">
                          <v-icon x-small left>{{ expandedIssueId === issue.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          {{ expandedIssueId === issue.id ? 'Hide row data' : 'Inspect row data' }}
                        </v-btn>
                      </td>
                      <td class="text-center">
                        <span class="cml-results-pill" :class="`cml-results-pill--${issue.statusType}`">{{ issue.action }}</span>
                      </td>
                    </tr>
                    <tr v-if="issue.rowData && expandedIssueId === issue.id" :key="`${issue.id}-details`">
                      <td colspan="4" class="cml-results-row-data">
                        <v-simple-table dense class="transparent">
                          <tbody>
                            <tr v-for="(value, key) in issue.rowData" :key="key">
                              <td class="font-weight-medium">{{ key }}</td>
                              <td class="cml-results-mono">{{ value }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </section>

          <div class="d-flex justify-space-between pt-2">
            <v-btn text @click="reset" :disabled="importing"><v-icon left>mdi-upload-outline</v-icon>Import another file</v-btn>
            <v-btn color="primary" @click="close"><v-icon left>mdi-check</v-icon>Done</v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import { excelParser } from '@/features/Registers/modules/excelParser';
  import { importCmls } from './cmlImportService';
  import { downloadCmlImportTemplate } from './cmlImportTemplate';
  import { buildHierarchyRows, buildItemizedIssues, countAppliedCmlRows } from './cmlImportResults';
  import { log, error as logError } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';
  import { $has } from '@/utils';

  // Target CML field names MUST match the backend CmlImportFields constants.
  const CML_FIELDS = [
    { field: 'cmlNo', label: 'CML No.' },
    { field: 'name', label: 'Name / Description' },
    { field: 'cmlType', label: 'CML Type' },
    { field: 'designThickness', label: 'Design Thickness' },
    { field: 'minimumThickness', label: 'Minimum Thickness' },
    { field: 'inspectionStrategy', label: 'Inspection Strategy' },
    { field: 'isoReference', label: 'ISO Reference' },
    { field: 'nextInspectionDate', label: 'Next Inspection Date' },
    { field: 'followUpDate', label: 'Follow-Up Date' },
    { field: 'observation', label: 'Observation' },
    { field: 'recommendation', label: 'Recommendation' },
  ];

  const SOURCE_SYSTEMS = ['MERIDIUM', 'SAP', 'MAXIMO', 'ENGAGE', 'OTHER'];

  // The single source of truth for how a finished import is presented. Keyed by
  // the LOWER-CASED CmlImportResponse.Status the API derives server-side (see the
  // MCP-readiness rule — the UI renders the verdict, it doesn't invent it).
  // `alert` non-null means the outcome gets its own callout instead of a caption.
  const RESULT_STATES = Object.freeze({
    completed: {
      key: 'completed',
      title: 'Import complete',
      icon: 'mdi-check-circle-outline',
      color: 'success',
      tone: 'success',
      alert: null,
      showStats: true,
      fallbackMessage: 'The import finished successfully.',
    },
    completedwithwarnings: {
      key: 'completedWithWarnings',
      title: 'Imported with warnings',
      icon: 'mdi-alert-circle-check-outline',
      color: 'warning',
      tone: 'warning',
      alert: null,
      showStats: true,
      fallbackMessage: 'The import finished — review the warnings below.',
    },
    partiallycompleted: {
      key: 'partiallyCompleted',
      title: 'Partially imported',
      icon: 'mdi-alert-outline',
      color: 'warning',
      tone: 'warning',
      alert: 'warning',
      showStats: true,
      fallbackMessage: 'Some rows were saved and some were not — review the details below.',
    },
    failed: {
      key: 'failed',
      title: 'Import failed',
      icon: 'mdi-close-circle-outline',
      color: 'error',
      tone: 'error',
      alert: 'error',
      // Nothing persisted — counters would only mislead (a legacy API even
      // inflates them on the rollback path), so the cards stay hidden.
      showStats: false,
      fallbackMessage: 'The import did not save anything, and the server returned no reason.',
    },
  });

  export default {
    name: 'CmlBulkImportDialog',
    props: {
      value: { type: Boolean, default: false },
    },
    data() {
      return {
        dialog: false,
        step: 1,
        steps: ['Upload', 'Map', 'Results'],
        selectedFile: null,
        fileProcessing: false,
        dragOver: false,
        excelData: null,
        cmlFields: CML_FIELDS,
        sourceSystems: SOURCE_SYSTEMS,
        masterTagColumn: null,
        externalRefColumn: null,
        tmlPointNoColumn: null,
        readingValueColumn: null,
        readingDateColumn: null,
        inspectionNoColumn: null,
        autoCreateInspections: false,
        externalRefSource: 'MERIDIUM',
        fieldColumns: {},
        importing: false,
        importResults: null,
        expandedIssueId: null,
      };
    },
    computed: {
      columns() {
        return (this.excelData && this.excelData.headers) || [];
      },
      columnItems() {
        return [{ text: '— Not mapped —', value: null }, ...this.columns.map((c) => ({ text: c, value: c }))];
      },
      canImport() {
        return !!this.masterTagColumn && !this.importing;
      },
      stepTitle() {
        return ['Upload your spreadsheet', 'Map your columns', 'Import results'][this.step - 1];
      },
      stepDescription() {
        return [
          'Select the file exported from your integrity system.',
          'Tell us which column is which. Smart suggestions are pre-filled — adjust as needed.',
          'Review what was imported.',
        ][this.step - 1];
      },
      stats() {
        return this.importResults && this.importResults.statistics;
      },
      errors() {
        return (this.importResults && this.importResults.errors) || [];
      },
      warnings() {
        return (this.importResults && this.importResults.warnings) || [];
      },
      hierarchyRows() {
        return buildHierarchyRows(this.stats);
      },
      appliedCmlRowCount() {
        return countAppliedCmlRows(this.stats);
      },
      itemizedIssues() {
        return buildItemizedIssues(this.errors, this.warnings);
      },
      // ONE derived outcome for the whole results step. Prefer the server's Status;
      // fall back only for an API that predates it.
      resultState() {
        const raw = this.importResults && this.importResults.status;
        const known = raw === null || raw === undefined ? null : RESULT_STATES[String(raw).trim().toLowerCase()];
        return known || this.fallbackResultState;
      },
      // Legacy API: its counters are incremented BEFORE the flush, so they can
      // claim rows a rollback threw away — `success` is the only honest signal.
      // The zero-persist check is a second net, never the primary test.
      fallbackResultState() {
        const r = this.importResults || {};
        if (r.success !== true) return RESULT_STATES.failed;
        const s = this.stats || {};
        const persisted = (s.newRows || 0) + (s.updatedRows || 0) + (s.mergedRows || 0) + (s.restoredRows || 0);
        if ((s.totalRows || 0) > 0 && persisted === 0) return RESULT_STATES.partiallycompleted;
        if (this.errors.length || this.warnings.length) return RESULT_STATES.completedwithwarnings;
        return RESULT_STATES.completed;
      },
      resultMessage() {
        return (this.importResults && this.importResults.message) || this.resultState.fallbackMessage;
      },
      // Creating inspections is an inspection WRITE — the server gates the flag on
      // this same permission and 403s the whole import without it.
      canAutoCreateInspections() {
        return $has(permissions.InspectionTags.Create);
      },
      inspectionNoHint() {
        const base = 'Links each reading to an existing inspection — the reading becomes read-only and feeds the inspection rollup.';
        // Without the switch beside it, this select must still explain what
        // happens to an inspection number that does not resolve.
        return this.canAutoCreateInspections ? base : `${base} Not found → imported as historical with a warning.`;
      },
      autoCreateInspectionsHint() {
        if (!this.inspectionNoColumn) return 'Map the Inspection No. column to use this.';
        return this.autoCreateInspections
          ? 'Missing inspection numbers are created and linked to the row\'s master tag. Only new readings link to them — readings imported earlier stay historical.'
          : 'Missing inspection numbers are imported as historical readings with a warning.';
      },
    },
    watch: {
      value(v) {
        this.dialog = v;
        if (v) this.reset();
      },
      dialog(v) {
        if (v !== this.value) this.$emit('input', v);
      },
      // Unmapping the column leaves auto-create with nothing to act on — drop it
      // rather than leaving a switched-on toggle that does nothing.
      inspectionNoColumn(v) {
        if (!v) this.autoCreateInspections = false;
      },
    },
    methods: {
      stepClass(n) {
        if (this.step > n) return 'done';
        if (this.step === n) return 'active';
        return 'pending';
      },
      triggerFileInput() {
        if (!this.fileProcessing) this.$refs.fileInput.click();
      },
      onFileInputChange(e) {
        const file = e.target.files && e.target.files[0];
        if (file) this.selectedFile = file;
      },
      onDrop(e) {
        this.dragOver = false;
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (!file) return;
        if (!/\.(xlsx?|csv)$/i.test(file.name)) {
          this.notifyError('Please choose a .xlsx, .xls or .csv file.');
          return;
        }
        this.selectedFile = file;
      },
      clearFile() {
        this.selectedFile = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      },
      // Generate + download a blank, headers-only template the user can fill in.
      // Headers are aligned to autoSuggest, so the filled file re-maps itself.
      downloadTemplate() {
        try {
          downloadCmlImportTemplate();
          log('CML import — template downloaded');
        } catch (err) {
          logError('CML import — template download failed:', err);
          this.notifyError('Could not generate the template file.');
        }
      },
      formatSize(bytes) {
        if (!bytes) return '0 B';
        const k = 1024;
        const units = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
      },
      async processFile() {
        if (!this.selectedFile) return;
        this.fileProcessing = true;
        try {
          const parsed = await excelParser.parse(this.selectedFile);
          const structure = excelParser.validateStructure(parsed);
          if (!structure.isValid) {
            throw new Error(structure.errors.join(', '));
          }
          (structure.warnings || []).forEach((w) => this.notifyWarning(w));
          parsed.data = excelParser.cleanData(parsed.data);
          this.excelData = parsed;
          this.autoSuggest();
          this.step = 2;
        } catch (err) {
          logError('CML import — file parse failed:', err);
          this.notifyError(`Could not read that file: ${err.message}`);
        } finally {
          this.fileProcessing = false;
        }
      },
      // Pre-seed every field key so Vue 2 tracks them — assigning a brand-new
      // object key via v-model is NOT reactive, so the <v-select> wouldn't update.
      blankFieldColumns() {
        const o = {};
        this.cmlFields.forEach((f) => { o[f.field] = null; });
        return o;
      },
      autoSuggest() {
        const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const cols = this.columns;
        const find = (cands) => cols.find((c) => cands.some((k) => norm(c).includes(k))) || null;

        this.masterTagColumn = find(['tagno', 'tagnumber', 'equipmenttag', 'assettag', 'tagname', 'tag']);
        this.externalRefColumn = find(['cmlid', 'externalref', 'externalid', 'meridiumid', 'sourceid', 'refid']);
        this.tmlPointNoColumn = find(['tmlno', 'tmlpointno', 'tmlpoint', 'measurementpoint', 'pointno', 'tml']);
        this.readingValueColumn = find(['lastreading', 'measuredthickness', 'wallthickness', 'currentthickness', 'lastthickness', 'readingmm']);
        this.readingDateColumn = find(['readingdate', 'measurementdate', 'surveydate', 'lastreadingdate']);
        this.inspectionNoColumn = find(['inspectionno', 'inspectionnumber', 'inspno', 'inspectionid', 'reportnumber']);

        const guesses = {
          cmlNo: ['cmlno', 'cmlnumber'],
          name: ['description', 'cmlname', 'location', 'name'],
          cmlType: ['cmltype'],
          designThickness: ['designthickness', 'nominalthickness', 'tnominal', 'designwt'],
          minimumThickness: ['minimumthickness', 'minthickness', 'tmin', 'retirement'],
          inspectionStrategy: ['inspectionstrategy', 'strategy'],
          isoReference: ['isoreference', 'isoref', 'drawing'],
          nextInspectionDate: ['nextinspection', 'nextinspdate', 'duedate'],
          followUpDate: ['followup'],
          observation: ['observation', 'notes', 'comment'],
          recommendation: ['recommendation', 'recommend', 'action'],
        };
        const fc = this.blankFieldColumns();
        Object.keys(guesses).forEach((field) => {
          const col = find(guesses[field]);
          // Don't reuse the columns already claimed as identity columns.
          if (col && col !== this.masterTagColumn && col !== this.externalRefColumn && col !== this.tmlPointNoColumn
              && col !== this.readingValueColumn && col !== this.readingDateColumn && col !== this.inspectionNoColumn) fc[field] = col;
        });
        this.fieldColumns = fc;
        log('CML import — auto-suggested mapping', { masterTagColumn: this.masterTagColumn, externalRefColumn: this.externalRefColumn, fieldColumns: fc });
      },
      buildMapping() {
        const fieldMappings = {};
        this.cmlFields.forEach((f) => {
          const col = this.fieldColumns[f.field];
          if (col) fieldMappings[col] = f.field;
        });
        return {
          fieldMappings,
          mandatoryFields: {},
          masterTagColumn: this.masterTagColumn,
          externalRefColumn: this.externalRefColumn || null,
          tmlPointNoColumn: this.tmlPointNoColumn || null,
          readingValueColumn: this.readingValueColumn || null,
          readingDateColumn: this.readingDateColumn || null,
          inspectionNoColumn: this.inspectionNoColumn || null,
        };
      },
      async performImport() {
        if (!this.canImport) return;
        this.importing = true;
        try {
          const rows = (this.excelData && this.excelData.data) || [];
          const result = await importCmls({
            rows,
            mapping: this.buildMapping(),
            externalRefSource: this.externalRefSource,
            fileName: this.selectedFile && this.selectedFile.name,
            // Re-checked against the permission and the mapping: a stale true
            // turns a working import into a whole-request 403.
            autoCreateInspections: this.canAutoCreateInspections
              && !!this.inspectionNoColumn
              && this.autoCreateInspections,
          });
          this.importResults = result || {};
          this.step = 3;
          log('CML import — result', { status: this.importResults.status, state: this.resultState.key });
          // Same derived state as the results screen — the toast can't disagree.
          if (this.resultState.tone === 'error') this.notifyError(this.resultMessage);
          else if (this.resultState.tone === 'warning') this.notifyWarning(this.resultMessage);
          else this.notifySuccess(this.resultMessage);
        } catch (err) {
          logError('CML import failed:', err);
          this.notifyError(`Import failed: ${(err.response && err.response.data && err.response.data.message) || err.message}`);
        } finally {
          this.importing = false;
        }
      },
      reset() {
        this.step = 1;
        this.selectedFile = null;
        this.excelData = null;
        this.fieldColumns = this.blankFieldColumns();
        this.masterTagColumn = null;
        this.externalRefColumn = null;
        this.tmlPointNoColumn = null;
        this.readingValueColumn = null;
        this.readingDateColumn = null;
        this.inspectionNoColumn = null;
        this.autoCreateInspections = false;
        this.externalRefSource = 'MERIDIUM';
        this.importResults = null;
        this.expandedIssueId = null;
        this.importing = false;
        this.fileProcessing = false;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      },
      toggleRowData(issueId) {
        this.expandedIssueId = this.expandedIssueId === issueId ? null : issueId;
      },
      close() {
        this.dialog = false;
      },
      // Notifications — guarded so a missing $dialog plugin never crashes the demo.
      notify(kind, message) {
        try {
          this.$dialog.notify[kind](message, { position: 'top-right', timeout: 4000 });
        } catch (err) {
          log(`[${kind}] ${message}`, err.message);
        }
      },
      notifySuccess(m) { this.notify('success', m); },
      notifyError(m) { this.notify('error', m); },
      notifyWarning(m) { this.notify('warning', m); },
    },
  };
</script>

<style scoped>
  .drop-area {
    border: 3px dashed var(--v-primary-base);
    border-radius: 14px;
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .drop-area:hover { background: rgba(0, 0, 0, 0.02); }
  .drop-area.drag-active { background: var(--v-primary-lighten3); }
  .drop-area.has-file { border-style: solid; border-color: var(--v-success-base); }

  .section-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--v-text-secondary, rgba(0, 0, 0, 0.6));
    margin-bottom: 8px;
  }

  /* Matches the persistent-hint caption of the selects it sits beside.
     Muted but readable on this white dialog surface. */
  .cml-import-hint {
    font-size: 0.75rem;
    line-height: 1.25rem;
    color: #5c6b66;
  }
  .cml-import-switch ::v-deep .v-label { font-size: 0.875rem; }

  .step-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
  }
  .step-circle.done { background: var(--v-success-base); color: #fff; }
  .step-circle.active { background: var(--v-primary-base); color: #fff; }
  .step-circle.pending { background: rgba(0, 0, 0, 0.08); color: rgba(0, 0, 0, 0.5); }

  .cml-results-container {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .cml-results-hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-space-md);
    padding: 12px var(--ds-space-md);
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-md);
  }
  .cml-results-hero--success { background: rgba(16, 185, 129, 0.08); border-color: rgba(16, 185, 129, 0.25); }
  .cml-results-hero--warning { background: rgba(245, 158, 11, 0.08); border-color: rgba(245, 158, 11, 0.25); }
  .cml-results-hero--error { background: rgba(239, 68, 68, 0.08); border-color: rgba(239, 68, 68, 0.25); }
  .cml-results-hero--info { background: rgba(2, 132, 199, 0.08); border-color: rgba(2, 132, 199, 0.25); }

  .cml-results-hero__summary,
  .cml-results-hero__badges {
    display: flex;
    align-items: center;
    gap: var(--ds-space-sm);
  }

  .cml-results-hero__summary {
    min-width: 0;
    gap: 12px;
  }

  .cml-results-hero__badges {
    flex-shrink: 0;
  }

  .cml-results-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-space-sm);
    margin-bottom: var(--ds-space-xs);
    color: var(--v-text-base);
    font-size: var(--ds-font-caption);
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .cml-results-section__header span:last-child {
    font-weight: 400;
    opacity: 0.65;
    text-transform: none;
  }

  .cml-results-table-wrap {
    overflow-x: auto;
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-md);
  }

  .cml-results-table {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;
    table-layout: fixed;
    color: var(--v-text-base);
    font-size: 0.8125rem;
  }

  .cml-results-table th,
  .cml-results-table td {
    box-sizing: border-box;
    padding: 9px 14px;
    text-align: left;
    vertical-align: middle;
  }

  .cml-results-table th {
    background: var(--ds-surface-hover);
    border-bottom: 1px solid var(--ds-border-subtle);
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .cml-results-table td {
    border-bottom: 1px solid var(--ds-border-subtle);
  }

  .cml-results-table tbody tr:last-child td {
    border-bottom: 0;
  }

  .cml-results-table tbody tr:hover {
    background: var(--ds-surface-hover);
  }

  .cml-results-table th:nth-child(1) { width: 24%; }
  .cml-results-table th:nth-child(2) { width: 46%; }
  .cml-results-table th:nth-child(3),
  .cml-results-table th:nth-child(4) { width: 15%; }

  .cml-results-table--issues th:nth-child(1) { width: 13%; }
  .cml-results-table--issues th:nth-child(2) { width: 17%; }
  .cml-results-table--issues th:nth-child(3) { width: 50%; }
  .cml-results-table--issues th:nth-child(4) { width: 20%; }

  .cml-results-mono,
  .cml-results-code {
    font-family: ui-monospace, "JetBrains Mono", Menlo, Consolas, monospace;
  }

  .cml-results-code,
  .cml-results-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-sm);
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .cml-results-pill {
    border-radius: var(--ds-radius-full);
  }

  .cml-results-code--success,
  .cml-results-pill--success { background: rgba(16, 185, 129, 0.12); border-color: rgba(16, 185, 129, 0.3); }
  .cml-results-code--warning,
  .cml-results-pill--warning { background: rgba(245, 158, 11, 0.12); border-color: rgba(245, 158, 11, 0.3); }
  .cml-results-code--error,
  .cml-results-pill--error { background: rgba(239, 68, 68, 0.12); border-color: rgba(239, 68, 68, 0.3); }

  .cml-results-row-data {
    background: var(--ds-surface-hover);
  }

  @media (max-width: 700px) {
    .cml-results-hero,
    .cml-results-section__header {
      align-items: flex-start;
      flex-direction: column;
    }
  }
</style>
